import React from 'react';
import { Provider } from 'react-redux';
import { configureStore } from '@reduxjs/toolkit';
import { fetchListings } from './services/listingsService';
import { resolveListingThumbnail } from '../../../utils/listingImage';

// Conditionally import ListingsCriteriaForm - it may not exist in all environments
let ListingsCriteriaForm: any = null;
try {
  // @ts-ignore - importing JSX component in TS file
  ListingsCriteriaForm = require('~/components/ListingsCriteriaForm').default;
} catch (error) {
  // ListingsCriteriaForm not available in this environment
}

interface ListingItem {
  id: string;
  title?: string;
  address?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  list_price?: number;
  beds?: number;
  baths?: number;
  sq_foot?: number;
  image_data?: string[];
}

interface EmailEditorListingsCriteriaFormProps {
  t: (key: string) => string;
  formData: any;
  lookupOptions: any;
  isFilterSearchLoading?: boolean;
  defaultLat: number;
  defaultLon: number;
  mobileView?: boolean;
  onChange: (changes: any) => void;
  onResultsCount?: (count: number) => void;
  setCriteriaResults: (listings: ListingItem[]) => void;
  setLoading: (loading: boolean) => void;
  loading?: boolean;
}

// Default form values that match what ListingsCriteriaForm expects
const getDefaultFormData = () => ({
  propertytype: 'residential',
  watchname: '',
  existingwatchname: '',
  notification: 't',
  pw_sendinterval: 'D',
  dostatuscodechangenotification: true,
  doreducednotification: true,
  doopenhousenotification: true,
  pricemin: 0,
  pricemax: 99999999,
  shortsale: 'f',
  reo: 'f',
  resale: 'f',
  style: 'all',
  acreagemin: '0',
  acreagemax: '99999',
  bedsmin: '0',
  bedsmax: '0',
  bathsmin: '0',
  bathsmax: '0',
  sq_footmin: '0',
  sq_footmax: '99999',
  sq_footofficemin: '0',
  sq_footofficemax: '99999',
  garagemin: '0',
  garagemax: '0',
  minstories: '',
  maxstories: '',
  buildingsmin: '0',
  buildingsmax: '99',
  unitsmin: '0',
  unitsmax: '99',
  videotour: '',
  golfcourse: '',
  propertyview: '',
  carport: '',
  virtualtourid: '',
  waterfront: '',
  basement: '',
  pool: '',
  communityname: '',
  streetaddress: '',
  schooldistrict: '',
  city: '',
  elementaryschool: '',
  state: '',
  jrhighschool: '',
  zip_code: '',
  highschool: '',
  county: '',
  area: '',
  subarea: '',
  description: '',
  subtype: '',
  type: '',
  year_built: '',
  images: '0',
  statuscode: 'active,contingent,pending,pending - show',
  zoning: '',
  searchby: 'list',
  lat: 0,
  lon: 0,
  topleftlon: 0,
  topleftlat: 0,
  bottomrightlat: 0,
  bottomrightlon: 0,
  zoomlevel: 13,
  Return_Array: true,
  Return_Query: false,
  luxuryProperty: 'f',
  featuredProperty: 'f',
});

// Mock Redux store with minimal state needed for ListingsCriteriaForm
const createMockStore = () => {
  //Note: Retrieve user data from the global store, then update the state to obtain the available MLS IDs and fetch lookup options.
  const globalUser =
    typeof window !== 'undefined' && (window as any)?.globalStore?.getState
      ? (window as any).globalStore.getState()?.user || {}
      : {};

  const appSettingsSlice = {
    name: 'appSettings',
    initialState: {
      residentialstylelist: 'House,Town House,Duplex,Half Duplex,Triplex,Fourplex,Condominium,Farm or Ranch,Manufactured Home',
      residential_subtype_list: 'Single Family,Condo,Townhouse,Mobile Home',
    },
    reducers: {},
  };

  const propertyWatchLocationSlice = {
    name: 'propertyWatchLocation',
    initialState: {
      lat: null,
      lon: null,
      address: '',
    },
    reducers: {
      setPropertyWatchLocation: (state: any, action: any) => {
        return { ...state, ...action.payload };
      },
      resetPropertyWatchLocation: () => {
        return { lat: null, lon: null, address: '' };
      },
    },
  };

  return configureStore({
    reducer: {
      appSettings: (state = appSettingsSlice.initialState) => state,
      propertyWatchLocation: propertyWatchLocationSlice.reducers.setPropertyWatchLocation ||
        ((state = propertyWatchLocationSlice.initialState) => state),
      user: (state = globalUser) => state,
    },
  });
};

/**
 * Adapter component that bridges the shared ListingsCriteriaForm
 * for use within the email editor's criteria drawer.
 * Wraps the form in a minimal Redux provider since the form uses Redux hooks.
 */
const EmailEditorListingsCriteriaForm: React.FC<EmailEditorListingsCriteriaFormProps> = ({
  t,
  formData,
  lookupOptions,
  isFilterSearchLoading,
  defaultLat,
  defaultLon,
  mobileView = false,
  onChange,
  onResultsCount,
  setCriteriaResults,
  setLoading,
  loading,
}) => {
  // Create store once and reuse - don't recreate on every render
  const store = React.useMemo(() => createMockStore(), []);

  // Keep a stable reference for formData to prevent unnecessary Formik reinitializations
  const [internalFormData, setInternalFormData] = React.useState<any>(() => {
    const defaults = getDefaultFormData();
    // Preserve property type from formData (check both naming conventions)
    const savedPropertyType = formData?.propertytype || formData?.propertyType;
    // Remove propertyType (camelCase) if it exists to avoid duplicates
    const { propertyType, ...cleanFormData } = formData || {};

    // Fix any array values that should be scalars (defensive coding)
    const fixedFormData = { ...cleanFormData };
    ['pricemin', 'pricemax', 'sq_footmin', 'sq_footmax', 'bedsmin', 'bedsmax', 'bathsmin', 'bathsmax'].forEach(key => {
      if (Array.isArray(fixedFormData[key])) {
        fixedFormData[key] = fixedFormData[key][0];
      }
    });

    return {
      ...defaults,
      ...fixedFormData,
      propertytype: savedPropertyType || defaults.propertytype,
      lat: formData?.lat ?? defaults.lat,
      lon: formData?.lon ?? defaults.lon,
    };
  });

  // Track if this is the initial mount
  const isInitialMount = React.useRef(true);

  // Update internal form data when external formData changes
  React.useEffect(() => {
    const defaults = getDefaultFormData();
    // Preserve property type from formData (check both naming conventions)
    const savedPropertyType = formData?.propertytype || formData?.propertyType;
    // Remove propertyType (camelCase) if it exists to avoid duplicates
    const { propertyType, ...cleanFormData } = formData || {};

    // Fix any array values that should be scalars (defensive coding)
    const fixedFormData = { ...cleanFormData };
    ['pricemin', 'pricemax', 'sq_footmin', 'sq_footmax', 'bedsmin', 'bedsmax', 'bathsmin', 'bathsmax'].forEach(key => {
      if (Array.isArray(fixedFormData[key])) {
        fixedFormData[key] = fixedFormData[key][0];
      }
    });

    const updated = {
      ...defaults,
      ...fixedFormData,
      propertytype: savedPropertyType || defaults.propertytype,
      lat: formData?.lat ?? defaults.lat,
      lon: formData?.lon ?? defaults.lon,
    };
    setInternalFormData(updated);

    // Only notify parent on initial mount to establish defaults
    // After that, handleUpdate will manage updates
    if (isInitialMount.current) {
      onChange(updated);
      isInitialMount.current = false;
      // Fetch initial count when form is initialized
      debouncedFetchCount(updated);
    }
  }, [formData]);

  // Debounce timer ref for API calls
  const debounceTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const [currentCount, setCurrentCount] = React.useState<number>(0);

  // Fetch listings based on current form data and update state in CBAY only
  const fetchCount = React.useCallback(async (formData: any) => {
    try {
      const {listings, count} = await fetchListings(formData, setLoading);
	  setCriteriaResults(listings.map((listing: any) => {
          const image = resolveListingThumbnail(listing);
          return {
            id: `${listing?.ml_number}|${listing?.mlsid}|${listing?.statuscode}|${listing?.reduceddatetime}`,
            title: listing?.streetaddress,
            address: `${listing?.streetaddress}`,
            city: listing?.city,
            state: listing?.state,
            zip: listing?.zip_code,
            list_price: listing?.list_price,
            mls: listing?.ml_number,
            dateListed: listing?.listingDisplayDate || listing?.LISTINGDISPLAYDATE || listing?.AddDateTime || listing?.adddatetime,
            image_data: image ? [image] : [],
			beds: listing?.beds || 0,
			baths: listing?.baths || 0,
			sq_foot: listing?.sq_foot || 0,
          };
        }));
      setCurrentCount(count);
      onResultsCount?.(count);
    } catch (error) {
      console.error('Error fetching listings count:', error);
      setCurrentCount(0);
      onResultsCount?.(0);
    }
  }, [onResultsCount]);

  // Debounced fetch to avoid too many API calls
  const debouncedFetchCount = React.useCallback((formData: any) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer
    debounceTimerRef.current = setTimeout(() => {
      fetchCount(formData);
    }, 500); // 500ms debounce
  }, [fetchCount]);

  // Cleanup timer on unmount
  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Stable callback that matches ListingsCriteriaForm's expected signature: (key, value)
  const handleUpdate = React.useCallback((key: string, value: any) => {
    // Update internal state immediately for smooth UX
    setInternalFormData((prev: any) => {
      const updated = { ...prev, [key]: value };
      // Also notify parent with the updated full state
      onChange(updated);
      // Fetch updated count with debounce
      debouncedFetchCount(updated);
      return updated;
    });
  }, [onChange, debouncedFetchCount]);

  // Translation mapper - maps ListingsCriteriaForm keys to English text
  // TODO: Import actual translations from ~/bay-ui/src/i18n/en/en.json if needed
  const translationMap: Record<string, string> = {
    // Property type & search
    'propertyType': 'Property Type',
    'priceRange': 'Price Range',
    'MIN': 'MIN',
    'MAX': 'MAX',
    'min': 'Min',
    'max': 'Max',

    // Search by
    'list': 'List',
    'map': 'Map',
    'byIndividualFields': 'By Individual Fields',
    'individualFieldsSubContent': 'Define separate locations using individual data points such as Communities, School Districts, Area, Sub Area, State/Province, Zip/Postal Code, and more.',
    'byMap': 'By Map',
    'bayMapSubContent': 'Define a single location area using a live map that you can zoom and drag to manually choose where you want listings to match within.',
    'propertyNewSearchLocationDesc': 'Set property locations by individual fields or using a live map. Listings will match only within the location criteria or map area you define.',

    // Type filters
    'type': 'Type',
    'shortsale': 'Shortsale',
    'resale': 'Resale',
    'foreclosure': 'Foreclosure',

    // Style
    'style': 'Style',

    // Size
    'size': 'Size',
    'beds': 'Beds',
    'baths': 'Baths',
    'sqFeetRange': 'SQ Feet Range',
    'acreage': 'Acreage',
    'garage': 'Garage',
    'stories': 'Stories',
    'buildings': 'Buildings',
    'units': 'Units',
    'SqFootOffice': 'SQ Foot Office',
    'SqFoot': 'SQ Foot',

    // More section
    'more': 'More',
    'yearBuilt': 'Year Built',
    'subType': 'Sub Type',
    'image': 'Image',
    'status': 'Status',

    // Features
    'onlyVideo': 'Display only with video tour',
    'golfCourse': 'Golf Course',
    'propertyView': 'Property View',
    'carPort': 'Car Port',
    'onlyVirtual': 'Display only with virtual tour',
    'waterFront': 'Water Front',
    'basement': 'Basement',
    'pool': 'Pool',
	'luxuryProperty': 'Global Luxury Property',
	'featuredProperty': 'Featured Property',

    // Locations section
    'locations': 'Locations',
    'location': 'Location',
    'features': 'Features',
    'year': 'Year Built',
    'community': 'Community',
    'streetAddress': 'Street Address',
    'commonInputPlaceholder': 'Enter street address...',
    'schoolDistrict': 'School District',
    'city': 'City',
    'elementarySchool': 'Elementary School',
    'stateProvince': 'State/Province',
    'jrHighSchool': 'Jr High School',
    'zipCode': 'Zip Code',
    'highSchool': 'High School',
    'county': 'County',
    'area': 'Area',
    'subarea': 'Sub Area',
    'description': 'Description',
    'descriptionPlaceholder': 'Enter a description...',

    // Map mode
    'mapContent': 'Zoom and move the map to get notified of listings that match your criteria and are within the selected area.',
  };

  // Stable translation function with fallback to map or key
  const stableT = React.useCallback((key: string) => {
    // Try the translation map first
    if (translationMap[key]) {
      return translationMap[key];
    }
    // Try the email editor's t function
    if (typeof t === 'function') {
      const translated = t(key);
      // If it returns something different than the key, use it
      if (translated && translated !== key) {
        return translated;
      }
    }
    // Fallback to the key itself
    return key;
  }, [t]);

  // If ListingsCriteriaForm is not available, show a fallback message
  if (!ListingsCriteriaForm) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        <p>{t('Criteria form is not available in this environment.')}</p>
        <p style={{ fontSize: '14px', marginTop: '10px' }}>
          {t('Criteria configuration is managed in the main application.')}
        </p>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <div
        style={{
          // Make search-by radio buttons full width in the email editor drawer
          '--search-by-item-width': '100%',
        } as React.CSSProperties}
        className="email-editor-listings-criteria-form"
      >
        <style>{`
          .email-editor-listings-criteria-form .d-flex.flex-column.align-items-center > div {
            width: 100% !important;
          }
        `}</style>
        <ListingsCriteriaForm
          t={stableT}
          lookupOptions={lookupOptions}
          newSearchFormData={internalFormData}
          updateFormHandler={handleUpdate}
          handleSubmit={() => {}}
          defaultLan={defaultLat}
          defaultLon={defaultLon}
          filterCount={currentCount}
          mobileView={mobileView}
          onFilterCountChange={(c: number) => {
            // This prop exists but may not be used by the shared component
            // We're handling count fetching ourselves via the service
          }}
		  setLoading={setLoading}
		  loading={loading}
        />
      </div>
    </Provider>
  );
};

export default EmailEditorListingsCriteriaForm;
